import { useMemo, useState } from "react";
import { Heart, ChevronRight, Bell, BellOff, Settings2, Lock, Crown } from "lucide-react";
import { useUserFavorites } from "@/hooks/useUserFavorites";
import { useAllTodaysGames, type TransformedGame } from "@/hooks/useLiveGames";
import { formatInTimeZone } from "date-fns-tz";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { AuthGate } from "./AuthGate";
import FavoriteTeamsModal from "./FavoriteTeamsModal";
import SubscriptionModal from "./SubscriptionModal";
import AuthModal from "./AuthModal";
import FavoriteNationSelector from "./FavoriteNationSelector";

const useLocalTime = (utcTime: string) => {
  return useMemo(() => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = new Date(utcTime);
    return formatInTimeZone(date, userTimezone, "h:mm a");
  }, [utcTime]);
};

const LEAGUE_COUNTRY_FLAGS: Record<string, string> = {
  NHL: "🇺🇸", AHL: "🇺🇸", ECHL: "🇺🇸", KHL: "🇷🇺", SHL: "🇸🇪",
  Allsvenskan: "🇸🇪", Liiga: "🇫🇮", Extraliga: "🇨🇿", DEL: "🇩🇪", NL: "🇨🇭",
};

interface FavoriteGameCardProps {
  game: TransformedGame;
}

const FavoriteGameCard = ({ game }: FavoriteGameCardProps) => {
  const localTime = useLocalTime(game.matchStartUTC);
  const flag = LEAGUE_COUNTRY_FLAGS[game.league] || "🏒";

  return (
    <div className="bg-secondary/50 rounded-xl p-4 border border-border hover:border-primary/30 transition-all group">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{flag}</span>
          <span className="text-xs font-medium text-muted-foreground">{game.league}</span>
        </div>
        {game.isLive ? (
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-score-live rounded-full animate-pulse" />
            <span className="text-xs font-semibold text-score-live uppercase">Live</span>
          </div>
        ) : (
          <span className="text-xs text-primary font-medium">{localTime}</span>
        )}
      </div>

      {/* Teams */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {game.homeLogo ? (
              <img src={game.homeLogo} alt={game.homeTeam} className="w-6 h-6 object-contain" />
            ) : (
              <div className="w-6 h-6 rounded bg-secondary flex items-center justify-center text-[10px] font-bold">
                {game.homeAbbr}
              </div>
            )}
            <span className="text-sm font-medium text-foreground">{game.homeTeam}</span>
          </div>
          <span className={`font-display text-xl font-bold ${game.isLive ? 'text-foreground' : 'text-muted-foreground'}`}>
            {game.isLive ? game.homeScore : '-'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {game.awayLogo ? (
              <img src={game.awayLogo} alt={game.awayTeam} className="w-6 h-6 object-contain" />
            ) : (
              <div className="w-6 h-6 rounded bg-secondary flex items-center justify-center text-[10px] font-bold">
                {game.awayAbbr}
              </div>
            )}
            <span className="text-sm font-medium text-foreground">{game.awayTeam}</span>
          </div>
          <span className={`font-display text-xl font-bold ${game.isLive ? 'text-foreground' : 'text-muted-foreground'}`}>
            {game.isLive ? game.awayScore : '-'}
          </span>
        </div>
      </div>

      {/* Live indicator */}
      {game.isLive && (
        <div className="mt-3 pt-3 border-t border-border/50">
          <span className="text-xs text-muted-foreground">
            {game.period} • {game.time}
          </span>
        </div>
      )}
    </div>
  );
};

export function YourTeamsSection() {
  const { favorites, hasFavorites, toggleAlerts } = useUserFavorites();
  const { data: games } = useAllTodaysGames();
  const { user, subscription } = useAuth();
  const { t } = useLanguage();
  const [showModal, setShowModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  
  const hasPaidAccess = subscription.subscribed && subscription.tier !== null;

  // Filter games involving favorite teams
  const favoriteGames = useMemo(() => {
    if (!games || !hasFavorites) return [];
    const favoriteTeamKeys = new Set(favorites.teams.map((t) => `${t.league}:${t.abbr}`));
    const favoriteLeagueIds = new Set(favorites.leagues);
    return games.filter((game) => {
      const homeKey = `${game.league}:${game.homeAbbr}`;
      const awayKey = `${game.league}:${game.awayAbbr}`;
      if (favoriteTeamKeys.has(homeKey) || favoriteTeamKeys.has(awayKey)) return true;
      if (favoriteLeagueIds.has(game.league)) return true;
      return false;
    }).sort((a, b) => {
      if (a.isLive && !b.isLive) return -1;
      if (!a.isLive && b.isLive) return 1;
      return new Date(a.matchStartUTC).getTime() - new Date(b.matchStartUTC).getTime();
    });
  }, [games, favorites, hasFavorites]);

  if (!user) {
    return (
      <>
        <section className="py-12 container mx-auto px-6">
          <div className="glass-card p-8 text-center border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
            <Lock className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-foreground mb-2">
              {t.yourTeams.signInTitle}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {t.yourTeams.signInDesc}
            </p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
            >
              {t.yourTeams.signIn}
            </button>
          </div>
        </section>
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      </>
    );
  }

  if (!hasFavorites) {
    return (
      <>
        <section className="py-12 container mx-auto px-6">
          <div className="glass-card p-8 text-center border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
            <Heart className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold text-foreground mb-2">
              {t.yourTeams.followTitle}
            </h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {t.yourTeams.followDesc}
            </p>
            {hasPaidAccess ? (
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Heart className="w-4 h-4" />
                {t.yourTeams.chooseTeams}
              </button>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-amber-500/10 to-amber-500/5 rounded-xl border border-amber-500/20 max-w-md mx-auto">
                  <div className="flex items-center gap-3 justify-center">
                    <Crown className="w-5 h-5 text-amber-400" />
                    <p className="text-sm font-medium text-foreground">{t.yourTeams.upgradePro}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSubscriptionModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Crown className="w-4 h-4" />
                  {t.yourTeams.upgradeNow}
                </button>
              </div>
            )}
          </div>
        </section>
        <FavoriteTeamsModal isOpen={showModal} onClose={() => setShowModal(false)} />
        <SubscriptionModal 
          isOpen={showSubscriptionModal} 
          onClose={() => setShowSubscriptionModal(false)} 
          onNeedAuth={() => setShowAuthModal(true)}
        />
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
      </>
    );
  }

  return (
    <>
      <section className="py-12 container mx-auto px-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Heart className="w-5 h-5 text-primary fill-primary" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold text-foreground">
                {t.yourTeams.title}
              </h2>
              <p className="text-sm text-muted-foreground">
                {favorites.teams.length} team{favorites.teams.length !== 1 ? 's' : ''} · {favorites.leagues.length} league{favorites.leagues.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasPaidAccess ? (
              <button
                onClick={() => toggleAlerts(!favorites.alertsEnabled)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  favorites.alertsEnabled
                    ? "bg-primary/10 text-primary"
                    : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                {favorites.alertsEnabled ? (
                  <Bell className="w-4 h-4" />
                ) : (
                  <BellOff className="w-4 h-4" />
                )}
                {favorites.alertsEnabled ? t.yourTeams.alertsOn : t.yourTeams.alertsOff}
              </button>
            ) : null}
            {hasPaidAccess ? (
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-secondary text-muted-foreground hover:text-foreground rounded-lg transition-colors"
              >
                <Settings2 className="w-4 h-4" />
                {t.yourTeams.edit}
              </button>
            ) : (
              <button
                onClick={() => setShowSubscriptionModal(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 rounded-lg transition-colors"
              >
                <Crown className="w-4 h-4" />
                {t.yourTeams.upgradeToEdit}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
          <div className="lg:col-span-1">
            <FavoriteNationSelector onOpenSubscription={() => setShowSubscriptionModal(true)} />
          </div>
          <div className="lg:col-span-3">
            {favoriteGames.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {favoriteGames.slice(0, 6).map((game) => (
                  <FavoriteGameCard key={game.id} game={game} />
                ))}
              </div>
            ) : (
              <div className="glass-card p-8 text-center h-full flex flex-col items-center justify-center">
                <p className="text-muted-foreground">
                  {t.yourTeams.noGames}
                </p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  {t.yourTeams.checkBack}
                </p>
              </div>
            )}
          </div>
        </div>

        {favoriteGames.length > 6 && (
          <div className="flex justify-center mt-6">
            <button className="flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors">
              {t.yourTeams.viewAll} {favoriteGames.length} games
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </section>
      <FavoriteTeamsModal isOpen={showModal} onClose={() => setShowModal(false)} />
      <SubscriptionModal 
        isOpen={showSubscriptionModal} 
        onClose={() => setShowSubscriptionModal(false)} 
        onNeedAuth={() => setShowAuthModal(true)}
      />
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
}

export default YourTeamsSection;
